import React, { useMemo, useState } from "react";
import "./App.css";
import { BrandMark } from "./components/BrandMark";
import { Container } from "./components/Container";
import { Section } from "./components/Section";
import { Navbar } from "./components/Navbar";
import { Button } from "./components/Button";
import { FeatureCard } from "./components/FeatureCard";
import { PostCard } from "./components/PostCard";
import { TestimonialCard } from "./components/TestimonialCard";
import { ContactForm } from "./components/ContactForm";
import { Footer } from "./components/Footer";
import { Gallery } from "./components/Gallery";

type NavItem = { id: string; label: string };

export default function App() {
  const navItems: NavItem[] = useMemo(
    () => [
      { id: "about", label: "Обо мне" },
      { id: "values", label: "Ценности" },
      { id: "blog", label: "Блог" },
      { id: "collab", label: "Сотрудничество" },
      { id: "contacts", label: "Контакты" }
    ],
    []
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="app">
      <a className="skipLink" href="#main">
        Перейти к содержанию
      </a>

      <Navbar
        brand={
          <div className="brand">
            <BrandMark />
            <div className="brandText">
              <div className="brandTitle">Кристина Глухова</div>
              <div className="brandSubtitle">блоггер • мама • лайфстайл</div>
            </div>
          </div>
        }
        items={navItems}
        isOpen={isMenuOpen}
        onToggle={() => setIsMenuOpen((v) => !v)}
        onNavigate={() => setIsMenuOpen(false)}
      />

      <main id="main">
        <header className="hero" aria-label="Обложка и ключевое сообщение">
          <Container>
            <div className="heroGrid">
              <div className="heroLeft">
                <div className="pill">
                  <span className="pillDot" aria-hidden="true" />
                  Личный бренд • искренний контент • тёплые смыслы
                </div>

                <h1 className="heroTitle">
                  Привет, я <span className="accent">Кристина</span>.
                  <br />
                  Я снимаю жизнь, материнство и вдохновляю на <span className="accent2">мягкие</span> перемены.
                </h1>

                <p className="heroText">
                  Этот сайт — коротко обо мне и о том, как мы можем работать вместе: интеграции, обзоры,
                  UGC, сценарии, Reels/Shorts и контент-поддержка брендов с тёплой аудиторией.
                </p>

                <div className="heroActions">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => document.getElementById("collab")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    Сотрудничество
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    Узнать обо мне
                  </Button>
                </div>

                <div className="heroStats" role="list" aria-label="Ключевые факты">
                  <div className="stat" role="listitem">
                    <div className="statValue">Лайфстайл</div>
                    <div className="statLabel">уют, семья, рутина без фильтров</div>
                  </div>
                  <div className="stat" role="listitem">
                    <div className="statValue">Материнство</div>
                    <div className="statLabel">поддержка, честность, эмпатия</div>
                  </div>
                  <div className="stat" role="listitem">
                    <div className="statValue">Контент</div>
                    <div className="statLabel">смыслы + эстетика + польза</div>
                  </div>
                </div>
              </div>

              <div className="heroRight" aria-label="Портрет и карточки">
                <div className="portraitCard">
                  <div className="portrait">
                    <div className="portraitGlow" aria-hidden="true" />
                    <div className="portraitInner" aria-hidden="true">
                      <div className="portraitBadge">Blogger &amp; Mom</div>
                      <div className="portraitName">Kristina Glukhova</div>
                      <div className="portraitHint">контент • уют • смысл</div>
                    </div>
                  </div>

                  <div className="floatingCards" aria-hidden="true">
                    <div className="miniCard">
                      <div className="miniTitle">Reels</div>
                      <div className="miniText">сценарий → съёмка → монтаж</div>
                    </div>
                    <div className="miniCard miniCardAlt">
                      <div className="miniTitle">UGC</div>
                      <div className="miniText">нативно, красиво, честно</div>
                    </div>
                    <div className="miniCard miniCardAlt2">
                      <div className="miniTitle">Обзоры</div>
                      <div className="miniText">уход, дом, детское, лайфстайл</div>
                    </div>
                  </div>
                </div>

                <div className="heroNote">
                  <div className="heroNoteIcon" aria-hidden="true">
                    ✦
                  </div>
                  <div>
                    <div className="heroNoteTitle">Тон моего блога</div>
                    <div className="heroNoteText">
                      спокойный, поддерживающий, без давления — про жизнь «как есть», но с любовью к деталям.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>

          <div className="heroBackdrop" aria-hidden="true" />
        </header>

        <Section
          id="about"
          eyebrow="Обо мне"
          title="Тёплый блог про семью, привычки и маленькие победы"
          subtitle="Я люблю понятные ритуалы, честные разговоры и контент, который хочется сохранить."
        >
          <div className="aboutGrid">
            <div className="aboutCard">
              <h3 className="cardTitle">Кто я</h3>
              <p className="cardText">
                Я — Кристина Глухова, блоггер и мама. В моём контенте много реальной жизни: семейные будни,
                забота о себе, уют дома, заметки про материнство и идеи, которые помогают не «успевать всё»,
                а жить в своём темпе.
              </p>
              <div className="tagRow" aria-label="Темы блога">
                <span className="tag">материнство</span>
                <span className="tag">лайфстайл</span>
                <span className="tag">дом и уют</span>
                <span className="tag">самозабота</span>
                <span className="tag">контент</span>
              </div>
            </div>

            <div className="aboutCard aboutCardAlt">
              <h3 className="cardTitle">Для брендов</h3>
              <p className="cardText">
                Делаю интеграции, обзоры и UGC-видео, где продукт выглядит естественно и красиво, а смысл —
                понятен. Подхожу к работе как к совместному проекту: сценарий, тон, визуальная подача и
                аккуратные формулировки.
              </p>
              <div className="checkList" role="list" aria-label="Что вы получаете">
                <div className="check" role="listitem">
                  <span className="checkIcon" aria-hidden="true">
                    ✓
                  </span>
                  <span>структуру и сценарий под задачу</span>
                </div>
                <div className="check" role="listitem">
                  <span className="checkIcon" aria-hidden="true">
                    ✓
                  </span>
                  <span>съёмку в живой обстановке (дом/город)</span>
                </div>
                <div className="check" role="listitem">
                  <span className="checkIcon" aria-hidden="true">
                    ✓
                  </span>
                  <span>монтаж, титры, динамика, субтитры</span>
                </div>
                <div className="check" role="listitem">
                  <span className="checkIcon" aria-hidden="true">
                    ✓
                  </span>
                  <span>бережную коммуникацию и дедлайны</span>
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section
          id="values"
          eyebrow="Ценности"
          title="Смыслы, которые держат мой контент"
          subtitle="Я за честность, красоту в простом и спокойный голос в ленте."
          variant="soft"
        >
          <div className="featuresGrid">
            <FeatureCard
              title="Искренность"
              text="Говорю так, как есть: без идеальности, но с уважением к себе и к аудитории."
              icon="♡"
            />
            <FeatureCard
              title="Эстетика без давления"
              text="Красиво — не значит сложно. Люблю простые решения и тёплые кадры."
              icon="✦"
            />
            <FeatureCard
              title="Польза в деталях"
              text="Советы, подборки, «как я делаю» — чтобы можно было применить уже сегодня."
              icon="⌁"
            />
            <FeatureCard
              title="Мягкое материнство"
              text="Поддерживаю, а не учу. Деликатные темы — аккуратно и бережно."
              icon="☾"
            />
            <FeatureCard
              title="Ответственность"
              text="Дедлайны, ТЗ, согласования — всё по-взрослому. Контент — как проект."
              icon="✓"
            />
            <FeatureCard
              title="Нативность"
              text="Реклама не ломает историю: продукт встроен в мой обычный день."
              icon="∞"
            />
          </div>
        </Section>

        <Section
          id="blog"
          eyebrow="Блог"
          title="Свежие заметки и темы"
          subtitle="Мини-статьи, которые можно читать между делами."
        >
          <div className="postsGrid">
            <PostCard
              title="Как я планирую неделю, когда времени мало"
              excerpt="Простая система без «идеального расписания»: 3 опоры, 1 список и 15 минут на порядок в голове."
              meta="Рутина • Планирование"
              time="3 мин"
            />
            <PostCard
              title="Нежный уход за собой в материнстве"
              excerpt="5 маленьких ритуалов, которые возвращают ресурс — без чувства вины и без «надо»."
              meta="Самозабота • Бьюти"
              time="4 мин"
            />
            <PostCard
              title="Дом, где легко дышать: уют без лишнего"
              excerpt="Как я упрощаю пространство, чтобы жить спокойнее: свет, текстуры, хранение и одна привычка."
              meta="Дом • Уют"
              time="5 мин"
            />
          </div>

          <div className="centerRow">
            <Button
              variant="secondary"
              size="md"
              onClick={() => document.getElementById("contacts")?.scrollIntoView({ behavior: "smooth" })}
            >
              Попросить темы для следующего поста
            </Button>
          </div>
        </Section>

        <Section
          id="gallery"
          eyebrow="Галерея"
          title="Настроение моего контента"
          subtitle="Тёплые оттенки, живые кадры и история в каждом видео."
          variant="soft"
        >
          <Gallery />
        </Section>

        <Section
          id="collab"
          eyebrow="Сотрудничество"
          title="Форматы, с которыми я работаю"
          subtitle="Подберём под задачу: узнаваемость, доверие, продажи или контент для ваших соцсетей."
        >
          <div className="collabGrid">
            <div className="priceCard">
              <div className="priceTop">
                <div className="priceTitle">Интеграции в блог</div>
                <div className="priceBadge">нативно</div>
              </div>
              <div className="priceText">
                Форматы: stories-серия, post/reels, обзор + личный опыт, подборка. Тон: доверительный, без давления.
              </div>
              <div className="divider" />
              <ul className="bullets">
                <li>обсуждение задачи и ЦА</li>
                <li>сценарий/структура на согласование</li>
                <li>съёмка + монтаж + субтитры</li>
                <li>аналитика после публикации</li>
              </ul>
              <Button
                variant="primary"
                size="md"
                onClick={() => document.getElementById("contacts")?.scrollIntoView({ behavior: "smooth" })}
              >
                Обсудить интеграцию
              </Button>
            </div>

            <div className="priceCard priceCardAlt">
              <div className="priceTop">
                <div className="priceTitle">UGC для бренда</div>
                <div className="priceBadge priceBadgeAlt">контент</div>
              </div>
              <div className="priceText">
                Видео и фото для ваших соцсетей/рекламы: распаковка, демонстрация, «день с продуктом», короткие советы.
              </div>
              <div className="divider" />
              <ul className="bullets">
                <li>скрипт и хук для первых секунд</li>
                <li>варианты кадров и света</li>
                <li>монтаж под формат платформы</li>
                <li>передача исходников по договорённости</li>
              </ul>
              <Button
                variant="secondary"
                size="md"
                onClick={() => document.getElementById("contacts")?.scrollIntoView({ behavior: "smooth" })}
              >
                Заказать UGC
              </Button>
            </div>

            <div className="priceCard priceCardGlow">
              <div className="priceTop">
                <div className="priceTitle">Контент-пакет</div>
                <div className="priceBadge priceBadgeAlt2">выгодно</div>
              </div>
              <div className="priceText">
                Серия единых по стилю роликов и фото, чтобы закрыть контент на 2–4 недели: быстро, системно, красиво.
              </div>
              <div className="divider" />
              <ul className="bullets">
                <li>контент-план на период</li>
                <li>батч-съёмка в один день</li>
                <li>шаблоны титров/обложек</li>
                <li>варианты под тест гипотез</li>
              </ul>
              <Button
                variant="primary"
                size="md"
                onClick={() => document.getElementById("contacts")?.scrollIntoView({ behavior: "smooth" })}
              >
                Собрать пакет
              </Button>
            </div>
          </div>

          <div className="testimonials">
            <div className="testimonialsHead">
              <div className="eyebrowSmall">Отзывы</div>
              <h3 className="h3">Как со мной в работе</h3>
            </div>
            <div className="testimonialsGrid">
              <TestimonialCard
                name="Мария, бренд детских товаров"
                text="Очень аккуратная подача, тёплый тон и сильный визуал. Сценарий был продуман, согласование — быстрое."
              />
              <TestimonialCard
                name="Алина, бьюти-бренд"
                text="Получили UGC-ролики, которые идеально легли в рекламу. Нативно, без «кричащих» обещаний — то, что нужно."
              />
              <TestimonialCard
                name="Екатерина, маркетолог"
                text="Ответственность и чувство стиля. Сроки соблюдены, правок минимум, коммуникация — приятная и ясная."
              />
            </div>
          </div>
        </Section>

        <Section
          id="contacts"
          eyebrow="Контакты"
          title="Давайте сделаем тёплый и сильный контент"
          subtitle="Напишите пару слов о задаче — я отвечу и предложу формат. Можно просто познакомиться."
          variant="soft"
        >
          <div className="contactGrid">
            <div className="contactInfoCard">
              <h3 className="cardTitle">Быстрое сообщение</h3>
              <p className="cardText">
                Оставьте заявку через форму — это удобнее всего. Если хотите, укажите ссылку на бренд/продукт и дедлайн.
              </p>

              <div className="infoRows" role="list" aria-label="Контактные данные">
                <div className="infoRow" role="listitem">
                  <div className="infoIcon" aria-hidden="true">
                    ✉
                  </div>
                  <div>
                    <div className="infoLabel">Email</div>
                    <a className="infoValue" href="mailto:hello@kristina-glukhova.example">
                      hello@kristina-glukhova.example
                    </a>
                  </div>
                </div>
                <div className="infoRow" role="listitem">
                  <div className="infoIcon" aria-hidden="true">
                    ⌁
                  </div>
                  <div>
                    <div className="infoLabel">Соцсети</div>
                    <div className="infoValue">Instagram / TikTok / YouTube Shorts</div>
                  </div>
                </div>
                <div className="infoRow" role="listitem">
                  <div className="infoIcon" aria-hidden="true">
                    ⏱
                  </div>
                  <div>
                    <div className="infoLabel">Срок ответа</div>
                    <div className="infoValue">обычно в течение 24–48 часов</div>
                  </div>
                </div>
              </div>

              <div className="contactCTA">
                <div className="ctaBox">
                  <div className="ctaTitle">Пресс-кит</div>
                  <div className="ctaText">Подготовлю по запросу: статистика, аудитория, примеры интеграций.</div>
                </div>
              </div>
            </div>

            <ContactForm />
          </div>
        </Section>
      </main>

      <Footer
        onNavigate={(id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
        items={navItems}
      />
    </div>
  );
}